import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getFormToken, saveResponse } from "./supabase";

// ── ESCALAS (mesmas do App.jsx) ────────────────────────────────────────────
const ESCALAS = {
  who5: {
    id: "who5",
    title: "Índice de Bem-Estar (WHO-5)",
    desc: "Indique para cada afirmação a que se aproxima mais do modo como você se tem sentido nas últimas duas semanas.",
    instrucao: "Durante as últimas duas semanas...",
    opcoes: [
      { label: "Todo o tempo", valor: 5 },
      { label: "A maior parte do tempo", valor: 4 },
      { label: "Mais de metade do tempo", valor: 3 },
      { label: "Menos de metade do tempo", valor: 2 },
      { label: "Algum tempo", valor: 1 },
      { label: "Nunca", valor: 0 },
    ],
    questoes: [
      "Tenho me sentido alegre e de bom humor.",
      "Tenho me sentido calmo/a e relaxado/a.",
      "Tenho me sentido ativo/a e energizado/a.",
      "Acordei me sentindo revigorado/a e descansado/a.",
      "Minha vida diária tem sido repleta de coisas que me interessam.",
    ],
    pontuacao: (soma) => {
      const pct = Math.round((soma / 25) * 100);
      if (soma < 13) return { nivel: "alto", label: "Pobre bem-estar", detalhe: `${soma}/25 (${pct}%)` };
      if (soma <= 17) return { nivel: "médio", label: "Bem-estar moderado", detalhe: `${soma}/25 (${pct}%)` };
      return { nivel: "baixo", label: "Bom bem-estar", detalhe: `${soma}/25 (${pct}%)` };
    },
  },
  swls: {
    id: "swls",
    title: "Satisfação com a Vida (SWLS)",
    desc: "Abaixo você encontrará cinco afirmações. Indique o quanto concorda ou discorda com cada uma delas.",
    instrucao: "Usando a escala de 1 a 7, marque sua resposta:",
    opcoes: [
      { label: "Concordo totalmente", valor: 7 },
      { label: "Concordo", valor: 6 },
      { label: "Concordo ligeiramente", valor: 5 },
      { label: "Nem concordo nem discordo", valor: 4 },
      { label: "Discordo ligeiramente", valor: 3 },
      { label: "Discordo", valor: 2 },
      { label: "Discordo totalmente", valor: 1 },
    ],
    questoes: [
      "Na maioria dos aspectos, minha vida é próxima ao meu ideal.",
      "As condições da minha vida são excelentes.",
      "Estou satisfeito(a) com minha vida.",
      "Dentro do possível, tenho conseguido as coisas importantes que quero da vida.",
      "Se pudesse viver uma segunda vez, não mudaria quase nada na minha vida.",
    ],
    pontuacao: (soma) => {
      if (soma >= 31) return { nivel: "baixo", label: "Extremamente Satisfeito", detalhe: `${soma}/35` };
      if (soma >= 26) return { nivel: "baixo", label: "Satisfeito", detalhe: `${soma}/35` };
      if (soma >= 21) return { nivel: "baixo", label: "Ligeiramente Satisfeito", detalhe: `${soma}/35` };
      if (soma === 20) return { nivel: "médio", label: "Neutro", detalhe: `${soma}/35` };
      if (soma >= 15) return { nivel: "médio", label: "Ligeiramente Insatisfeito", detalhe: `${soma}/35` };
      if (soma >= 10) return { nivel: "alto", label: "Insatisfeito", detalhe: `${soma}/35` };
      return { nivel: "alto", label: "Extremamente Insatisfeito", detalhe: `${soma}/35` };
    },
  },
  gad7: {
    id: "gad7",
    title: "Escala de Ansiedade (GAD-7)",
    desc: "Durante as últimas 2 semanas, com que frequência você foi incomodado/a pelos problemas abaixo?",
    instrucao: "Nas últimas 2 semanas, com que frequência...",
    opcoes: [
      { label: "Nenhuma vez", valor: 0 },
      { label: "Vários dias", valor: 1 },
      { label: "Mais da metade dos dias", valor: 2 },
      { label: "Quase todos os dias", valor: 3 },
    ],
    questoes: [
      "Sentir-se nervoso/a, ansioso/a ou no limite.",
      "Não conseguir parar ou controlar as preocupações.",
      "Preocupar-se demais com diferentes coisas.",
      "Dificuldade em relaxar.",
      "Ficar tão agitado/a que se torna difícil ficar parado/a.",
      "Ficar facilmente irritado/a ou irritável.",
      "Sentir medo de que algo terrível possa acontecer.",
    ],
    pontuacao: (soma) => {
      if (soma <= 4)  return { nivel: "baixo", label: "Ansiedade Mínima", detalhe: `${soma}/21` };
      if (soma <= 9)  return { nivel: "médio", label: "Ansiedade Leve", detalhe: `${soma}/21` };
      if (soma <= 14) return { nivel: "alto",  label: "Ansiedade Moderada", detalhe: `${soma}/21` };
      return { nivel: "alto", label: "Ansiedade Grave", detalhe: `${soma}/21` };
    },
  },
  eps10: {
    id: "eps10",
    title: "Escala de Estresse Percebido (EPS-10)",
    desc: "As questões abaixo perguntam sobre seus sentimentos e pensamentos durante os últimos 30 dias.",
    instrucao: "Nos últimos 30 dias, com que frequência...",
    invertidos: [3, 4, 6, 7],
    opcoes: [
      { label: "Nunca", valor: 0 },
      { label: "Quase Nunca", valor: 1 },
      { label: "Às Vezes", valor: 2 },
      { label: "Pouco Frequente", valor: 3 },
      { label: "Muito Frequente", valor: 4 },
    ],
    questoes: [
      "Você ficou aborrecido por causa de algo que aconteceu inesperadamente?",
      "Você sentiu que foi incapaz de controlar coisas importantes na sua vida?",
      "Você esteve nervoso ou estressado?",
      "Você esteve confiante em sua capacidade de lidar com seus problemas pessoais?",
      "Você sentiu que as coisas aconteceram da maneira que você esperava?",
      "Você achou que não conseguiria lidar com todas as coisas que tinha por fazer?",
      "Você foi capaz de controlar irritações na sua vida?",
      "Você sentiu que todos os aspectos de sua vida estavam sob controle?",
      "Você esteve bravo por causa de coisas que estiveram fora de seu controle?",
      "Você sentiu que os problemas acumularam tanto que você não conseguiria resolvê-los?",
    ],
    pontuacao: (soma) => {
      if (soma <= 13) return { nivel: "baixo", label: "Estresse Baixo", detalhe: `${soma}/40` };
      if (soma <= 26) return { nivel: "médio", label: "Estresse Moderado", detalhe: `${soma}/40` };
      return { nivel: "alto", label: "Estresse Elevado", detalhe: `${soma}/40` };
    },
  },
};

// ── ESTILOS ────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600&family=DM+Sans:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #F0F7F7;
    color: #0D2626;
    min-height: 100vh;
  }

  .sf-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px 60px;
  }

  .sf-header {
    width: 100%;
    max-width: 600px;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 32px;
  }

  .sf-logo-name {
    font-family: 'Fraunces', serif;
    font-size: 20px;
    font-weight: 500;
    color: #1E7B78;
  }

  .sf-card {
    width: 100%;
    max-width: 600px;
    background: white;
    border-radius: 20px;
    padding: 32px 28px;
    box-shadow: 0 2px 16px rgba(30,123,120,.08);
    margin-bottom: 20px;
  }

  .sf-title {
    font-family: 'Fraunces', serif;
    font-size: 22px;
    font-weight: 500;
    color: #0D2626;
    margin-bottom: 8px;
  }

  .sf-desc {
    font-size: 14px;
    color: #4A6262;
    line-height: 1.6;
    margin-bottom: 6px;
  }

  .sf-instrucao {
    font-size: 13px;
    color: #1E7B78;
    font-weight: 500;
    background: #E6F4F3;
    padding: 8px 14px;
    border-radius: 8px;
    margin-top: 12px;
  }

  .sf-progress-bar {
    width: 100%;
    max-width: 600px;
    height: 6px;
    background: #E6F4F3;
    border-radius: 99px;
    margin-bottom: 24px;
    overflow: hidden;
  }

  .sf-progress-fill {
    height: 100%;
    background: #1E7B78;
    border-radius: 99px;
    transition: width .3s ease;
  }

  .sf-progress-label {
    width: 100%;
    max-width: 600px;
    font-size: 12px;
    color: #6B9090;
    margin-bottom: 8px;
    text-align: right;
  }

  .sf-question {
    width: 100%;
    max-width: 600px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 12px rgba(30,123,120,.07);
    margin-bottom: 16px;
  }

  .sf-q-num {
    font-size: 11px;
    font-weight: 600;
    color: #1E7B78;
    text-transform: uppercase;
    letter-spacing: .5px;
    margin-bottom: 8px;
  }

  .sf-q-text {
    font-size: 16px;
    font-weight: 500;
    color: #0D2626;
    line-height: 1.5;
    margin-bottom: 16px;
  }

  .sf-option {
    width: 100%;
    padding: 12px 16px;
    border: 1.5px solid #D4E8E7;
    border-radius: 10px;
    background: white;
    color: #0D2626;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    margin-bottom: 8px;
    transition: all .15s;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .sf-option:hover {
    border-color: #1E7B78;
    background: #F0F7F7;
  }

  .sf-option.selected {
    border-color: #1E7B78;
    background: #E6F4F3;
    color: #1E7B78;
    font-weight: 600;
  }

  .sf-option-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #9DD0CE;
    flex-shrink: 0;
    transition: all .15s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .sf-option.selected .sf-option-dot {
    background: #1E7B78;
    border-color: #1E7B78;
  }

  .sf-option.selected .sf-option-dot::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: white;
    display: block;
  }

  .sf-submit-area {
    width: 100%;
    max-width: 600px;
    margin-top: 8px;
  }

  .sf-submit-btn {
    width: 100%;
    padding: 16px;
    background: #1E7B78;
    color: white;
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: background .15s;
  }

  .sf-submit-btn:hover:not(:disabled) { background: #175f5d; }
  .sf-submit-btn:disabled { opacity: .6; cursor: not-allowed; }

  .sf-pending-msg {
    font-size: 13px;
    color: #6B9090;
    text-align: center;
    margin-top: 12px;
  }

  /* Tela de sucesso */
  .sf-success {
    width: 100%;
    max-width: 600px;
    background: white;
    border-radius: 20px;
    padding: 48px 32px;
    text-align: center;
    box-shadow: 0 2px 16px rgba(30,123,120,.08);
  }

  .sf-success-icon {
    font-size: 56px;
    margin-bottom: 20px;
    display: block;
  }

  .sf-success-title {
    font-family: 'Fraunces', serif;
    font-size: 24px;
    font-weight: 500;
    color: #0D2626;
    margin-bottom: 12px;
  }

  .sf-success-msg {
    font-size: 15px;
    color: #4A6262;
    line-height: 1.6;
  }

  /* Tela de erro */
  .sf-error {
    width: 100%;
    max-width: 600px;
    background: white;
    border-radius: 20px;
    padding: 48px 32px;
    text-align: center;
    box-shadow: 0 2px 16px rgba(30,123,120,.08);
  }

  .sf-footer {
    margin-top: 40px;
    font-size: 12px;
    color: #9DB8B8;
    text-align: center;
  }

  @media (max-width: 480px) {
    .sf-card { padding: 24px 20px; }
    .sf-question { padding: 20px 16px; }
    .sf-q-text { font-size: 15px; }
  }
`;

// ── LOGO SVG ───────────────────────────────────────────────────────────────
function OuvyLogo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="22" fill="#1E7B78" opacity="0.15" stroke="#1E7B78" strokeWidth="1"/>
      <circle cx="24" cy="10" r="4" fill="#1E7B78"/>
      <circle cx="38" cy="30" r="4" fill="#4AAE6E"/>
      <circle cx="10" cy="30" r="4" fill="#6B9E5E"/>
      <circle cx="24" cy="28" r="3.5" fill="#9DD0CE"/>
      <line x1="24" y1="14" x2="24" y2="24.5" stroke="#9DD0CE" strokeWidth="1.5"/>
      <line x1="21.5" y1="11" x2="12" y2="27" stroke="#4AAE6E" strokeWidth="1.5"/>
      <line x1="26.5" y1="11" x2="36" y2="27" stroke="#1E7B78" strokeWidth="1.5"/>
      <line x1="27.5" y1="28" x2="34.5" y2="30" stroke="#9DD0CE" strokeWidth="1.5"/>
      <line x1="20.5" y1="28" x2="13.5" y2="30" stroke="#9DD0CE" strokeWidth="1.5"/>
    </svg>
  );
}

// ── COMPONENTE PRINCIPAL ───────────────────────────────────────────────────
export default function StudentForm() {
  const { formId } = useParams();
  // formId format: "swls-<uuid>" ou apenas "<uuid>" (token direto)
  const parts = formId?.split("-");
  const knownEscalas = ["who5", "swls", "gad7", "eps10"];
  const escalaIdFromUrl = knownEscalas.includes(parts?.[0]) ? parts[0] : null;
  const tokenId = escalaIdFromUrl ? parts.slice(1).join("-") : formId;

  const [escalaId, setEscalaId]   = useState(escalaIdFromUrl);
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading]     = useState(!!tokenId && !escalaIdFromUrl);
  const [notFound, setNotFound]   = useState(false);
  const [respostas, setRespostas] = useState({});
  const [enviado, setEnviado]     = useState(false);
  const [enviando, setEnviando]   = useState(false);
  const [erro, setErro]           = useState("");
  const [jaRespondido, setJaRespondido] = useState(false);

  const escala = ESCALAS[escalaId];
  const totalQ      = escala?.questoes.length || 0;
  const respondidas = Object.keys(respostas).length;
  const completo    = respondidas === totalQ;
  const progresso   = totalQ > 0 ? Math.round((respondidas / totalQ) * 100) : 0;

  // Load token from Supabase if we have a UUID
  useEffect(() => {
    if (!tokenId || escalaIdFromUrl) return;
    async function loadToken() {
      const { data, error } = await getFormToken(tokenId);
      if (error || !data) { setNotFound(true); setLoading(false); return; }
      setEscalaId(data.escala_id);
      if (data.status === "answered") { setJaRespondido(true); setEnviado(true); setLoading(false); return; }
      setTokenData(data);
      setLoading(false);
    }
    loadToken();
  }, [tokenId, escalaIdFromUrl]);

  function selecionar(qIdx, valor) {
    setRespostas(prev => ({ ...prev, [qIdx]: valor }));
  }

  function calcularPontuacao() {
    if (!escala) return { soma: 0, nivel: "baixo", label: "" };
    const soma = Object.entries(respostas).reduce((acc, [idx, val]) => {
      const isInvertido = escala.invertidos && escala.invertidos.includes(Number(idx));
      const maxVal = escala.opcoes[0].valor;
      return acc + (isInvertido ? maxVal - val : val);
    }, 0);
    const resultado = escala.pontuacao ? escala.pontuacao(soma) : { nivel: "baixo", label: "Concluído", detalhe: String(soma) };
    return { soma, nivel: resultado.nivel, label: resultado.label };
  }

  async function enviar() {
    if (!completo) return;
    setEnviando(true);
    const { soma, nivel, label } = calcularPontuacao();
    setErro("");
    if (tokenData) {
      const { error } = await saveResponse({
        tokenId: tokenData.id,
        respostas,
        pontuacao: soma,
        nivelRisco: nivel,
        labelResultado: label,
      });
      if (error) {
        console.error("[Ouvy] Erro ao salvar resposta:", error);
        setErro("Não foi possível enviar suas respostas. Verifique a conexão e tente novamente.");
        setEnviando(false);
        return;
      }
    } else {
      await new Promise(r => setTimeout(r, 1000));
    }
    setEnviado(true);
    setEnviando(false);
  }

  // Loading
  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="sf-page">
          <div className="sf-header"><OuvyLogo /><span className="sf-logo-name">Ouvy</span></div>
          <div className="sf-success" style={{textAlign:"center",paddingTop:64}}>
            <div style={{fontSize:40,marginBottom:16}}>⏳</div>
            <div className="sf-success-title">Carregando formulário...</div>
          </div>
        </div>
      </>
    );
  }

  // Formulário não encontrado
  if (!escala || notFound) {
    return (
      <>
        <style>{css}</style>
        <div className="sf-page">
          <div className="sf-header">
            <OuvyLogo />
            <span className="sf-logo-name">Ouvy</span>
          </div>
          <div className="sf-error">
            <span style={{fontSize:48,display:"block",marginBottom:16}}>🔍</span>
            <div className="sf-success-title">Formulário não encontrado</div>
            <p className="sf-success-msg" style={{marginTop:8}}>
              O link pode ter expirado ou estar incorreto.<br/>
              Entre em contato com sua escola para obter um novo link.
            </p>
          </div>
        </div>
      </>
    );
  }

  // Tela de sucesso após envio
  if (enviado) {
    return (
      <>
        <style>{css}</style>
        <div className="sf-page">
          <div className="sf-header">
            <OuvyLogo />
            <span className="sf-logo-name">Ouvy</span>
          </div>
          <div className="sf-success">
            <span className="sf-success-icon">✅</span>
            <div className="sf-success-title">{jaRespondido ? "Formulário já respondido" : "Respostas enviadas!"}</div>
            <p className="sf-success-msg">
              {jaRespondido
                ? "Este link já foi utilizado e as respostas já estão registradas. Se precisar responder novamente, peça um novo link à sua escola."
                : "Obrigado por responder. Suas respostas foram registradas com segurança e ajudarão a equipe de psicologia a cuidar melhor do seu bem-estar."}
            </p>
            <p className="sf-success-msg" style={{marginTop:16,fontSize:13,color:"#9DB8B8"}}>
              Você já pode fechar esta página.
            </p>
          </div>
          <div className="sf-footer">Ouvy — Monitoramento Psicológico Escolar</div>
        </div>
      </>
    );
  }

  // Formulário
  return (
    <>
      <style>{css}</style>
      <div className="sf-page">

        {/* Header */}
        <div className="sf-header">
          <OuvyLogo />
          <span className="sf-logo-name">Ouvy</span>
        </div>

        {/* Card de apresentação */}
        <div className="sf-card">
          <div className="sf-title">{escala.title}</div>
          <p className="sf-desc">{escala.desc}</p>
          <div className="sf-instrucao">{escala.instrucao}</div>
        </div>

        {/* Barra de progresso */}
        <div className="sf-progress-label">{respondidas} de {totalQ} questões respondidas</div>
        <div className="sf-progress-bar">
          <div className="sf-progress-fill" style={{width: `${progresso}%`}} />
        </div>

        {/* Questões */}
        {escala.questoes.map((q, i) => (
          <div key={i} className="sf-question">
            <div className="sf-q-num">Questão {i + 1} de {totalQ}</div>
            <div className="sf-q-text">{q}</div>
            {escala.opcoes.map(op => (
              <button
                key={op.valor}
                className={`sf-option ${respostas[i] === op.valor ? "selected" : ""}`}
                onClick={() => selecionar(i, op.valor)}
              >
                <span className="sf-option-dot" />
                {op.label}
              </button>
            ))}
          </div>
        ))}

        {/* Botão de envio */}
        <div className="sf-submit-area">
          <button
            className="sf-submit-btn"
            onClick={enviar}
            disabled={!completo || enviando}
          >
            {enviando ? "Enviando..." : completo ? "Enviar respostas →" : `Responda todas as ${totalQ} questões para enviar`}
          </button>
          {erro && (
            <p className="sf-pending-msg" style={{color:"#C0392B",fontWeight:500}}>{erro}</p>
          )}
          {!completo && (
            <p className="sf-pending-msg">
              Faltam {totalQ - respondidas} {totalQ - respondidas === 1 ? "questão" : "questões"} para finalizar.
            </p>
          )}
        </div>

        <div className="sf-footer">
          Ouvy — Monitoramento Psicológico Escolar<br/>
          Suas respostas são confidenciais e protegidas pela LGPD.
        </div>
      </div>
    </>
  );
}
